import { FILAMENT_MAPPINGS } from "./filamentProfiles";

type FilamentMappings = typeof FILAMENT_MAPPINGS;
type FilamentTypeSettings = FilamentMappings[keyof FilamentMappings];
type ModelSpecificSettings = FilamentTypeSettings["model_settings"][string];
type FilamentId = keyof FilamentMappings & string;

export interface FilamentByIdResult {
    filamentId: FilamentId;
    materialType: string;
    profile: FilamentTypeSettings;
    nozzleTempMinInt: number;
    nozzleTempMaxInt: number;
}

export interface ModelSettingsBySettingIdResult {
    filamentId: FilamentId;
    materialType: string;
    printerModel: string;
    settings: ModelSpecificSettings;
    nozzleTempMinInt: number;
    nozzleTempMaxInt: number;
}

/**
 * Find a filament profile by its filament_id (e.g., "GFA05").
 */
export function findFilamentByFilamentId(filamentId: string): FilamentByIdResult | null {
    const needle = filamentId.trim();
    const profile = FILAMENT_MAPPINGS[needle as keyof FilamentMappings];
    if (!profile) return null;
    const { nozzleTempMinInt, nozzleTempMaxInt } = computeNozzleTempRange(profile);
    return { filamentId: needle as FilamentId, materialType: profile.materialType, profile, nozzleTempMinInt, nozzleTempMaxInt };
}

/**
 * Find model-specific settings by its setting_id (e.g., "GFSA05_03").
 */
export function findModelSettingsBySettingId(settingId: string): ModelSettingsBySettingIdResult | null {
    const needle = settingId.trim();
    for (const [fid, profile] of Object.entries(FILAMENT_MAPPINGS)) {
        for (const printerModel in profile.model_settings) {
            const settings = profile.model_settings[printerModel];
            if (settings?.setting_id === needle) {
                const { nozzleTempMinInt, nozzleTempMaxInt } = computeNozzleTempRange(profile, settings);
                return { filamentId: fid as FilamentId, materialType: profile.materialType, printerModel, settings, nozzleTempMinInt, nozzleTempMaxInt };
            }
        }
    }
    return null;
}

/**
 * For Bambu profiles, tray_info_idx corresponds to the base config id, i.e., filament_id.
 * So this delegates to findFilamentByFilamentId.
 */
export function findFilamentByTrayInfoIdx(trayInfoIdx: string): FilamentByIdResult | null {
    return findFilamentByFilamentId(trayInfoIdx);
}

function parseFirstInt(value: unknown): number {
    if (Array.isArray(value) && value.length > 0) {
        const n = parseInt(String(value[0]), 10);
        return Number.isFinite(n) ? n : 0;
    }
    if (typeof value === 'string' || typeof value === 'number') {
        const n = parseInt(String(value), 10);
        return Number.isFinite(n) ? n : 0;
    }
    return 0;
}

function computeNozzleTempRange(profile: FilamentTypeSettings, modelSettings?: ModelSpecificSettings): { nozzleTempMinInt: number; nozzleTempMaxInt: number } {
    // Prefer model-specific overrides when present
    const modelLow = modelSettings?.nozzle_temperature_range_low;
    const modelHigh = modelSettings?.nozzle_temperature_range_high;

    // Base defaults
    const base = profile.base_settings as Record<string, unknown>;
    const baseLow = base?.nozzle_temperature_range_low;
    const baseHigh = base?.nozzle_temperature_range_high;

    // Fallbacks if range is not provided: use nozzle_temperature or initial layer
    const fallback = base?.nozzle_temperature ?? base?.nozzle_temperature_initial_layer;

    const low = modelLow ?? baseLow ?? fallback ?? 0;
    const high = modelHigh ?? baseHigh ?? base?.nozzle_temperature ?? base?.nozzle_temperature_initial_layer ?? low ?? 0;

    return {
        nozzleTempMinInt: parseFirstInt(low),
        nozzleTempMaxInt: parseFirstInt(high),
    };
}
